// DATA & SAFETY — one pipeline for bringing data in (legacy VÉRO v1–v910 or AVRASTRO OS backups):
// analyse → preview (counts, warnings, diff) → safety snapshot → transactional commit → verify
// → automatic rollback on mismatch. Plus backups, snapshots/rollback and diagnostics.
import { useEffect, useMemo, useState } from 'react';
import { TABLE_NAMES, validateDataset, saveSetting, type TableName } from '@vero/domain';
import { catalogReport, LOCALES } from '@vero/i18n';
import type { SnapshotMeta } from '@vero/store';
import {
  parseSource, previewRestore, commitRestore, rollbackToSnapshot, createFullBackup, restoreFiles, serializeBackup, encryptBackup, BackupError,
  type ParsedSource, type RestorePreview, type RestoreResult,
} from '@vero/backup';
import { useRuntime, errorLog } from '../core/runtime.tsx';
import { Empty, Field, useConfirm } from '../core/ui.tsx';
import { Icon } from '../core/icons.tsx';
import { download } from '../core/links.ts';
import { navigate } from '../core/router.ts';
import { useSaveStatus, markBackupExport, backupAgeDays } from '../core/save-status.ts';
import { EDITION } from '../core/edition.ts';
import { ClientImportCard } from './client-import.tsx';

const MAIN_TABLES: TableName[] = ['persons', 'policies', 'cases', 'tasks', 'notes', 'reviews', 'prospect_lists', 'prospects', 'documents', 'service_cases', 'households', 'timeline', 'legacy_records'];
const stamp = () => new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-');
/** hand-over from other screens (e.g. a cloud backup downloaded in Account): the file goes through the same
 *  analyse → preview → snapshot → commit → verify pipeline as a file chosen here */
let QUEUED: { text: string; name: string } | null = null;
export function queueImport(text: string, name: string) { QUEUED = { text, name }; }

export function DataView() {
  const rt = useRuntime();
  const { t } = rt;
  return (
    <div>
      <div className="page-head"><div><h1>{t('data.title')}</h1><p>{t('data.subtitle')}</p></div></div>
      <div className="stack" style={{ gap: 18 }}>
        <ProtectionCard />
        <ClientImportCard />
        <ImportWizard />
        <div className="grid grid-2">
          <BackupCard />
          <Snapshots />
        </div>
        <Diagnostics />
      </div>
    </div>
  );
}


/** Save status in the top bar: saved on this device · not saved (memory only) · write error; offline state; backup reminder. */
export function SaveBadge() {
  const rt = useRuntime();
  const { t, i18n } = rt;
  const s = useSaveStatus();
  const age = backupAgeDays(s);
  const due = !s.volatile && (age === null ? rt.view.rows('persons').length > 0 : age >= 7);
  const state = s.volatile ? 'volatile' : s.error ? 'error' : 'saved';
  const time = s.savedAt ? i18n.dateTime(s.savedAt) : null;
  const title = [
    state === 'volatile' ? t('save.volatile') : state === 'error' ? t('save.error') : time ? t('save.saved_at', { time }) : t('save.auto'),
    s.online ? null : t('save.offline'),
    due ? (age === null ? t('save.backup_never') : t('save.backup_due', { days: age })) : null,
  ].filter(Boolean).join(' · ');
  return (
    <button className={`btn btn-sm save-badge ${state}${due ? ' due' : ''}${s.online ? '' : ' offline'}`} onClick={() => navigate('/data')} title={title} aria-label={title} data-testid="save-badge" data-state={state}>
      <Icon name={state === 'saved' ? 'check' : 'shield'} size={15} />
      <span className="save-badge-text">{state === 'volatile' ? t('save.short_volatile') : state === 'error' ? t('save.short_error') : t('save.short_saved')}</span>
      {!s.online && <span className="save-badge-off">{t('save.short_offline')}</span>}
    </button>
  );
}

/** Where the data lives and how it is protected — plain facts for the person who owns the data. */
function ProtectionCard() {
  const rt = useRuntime();
  const { t, i18n } = rt;
  const s = useSaveStatus();
  const encrypted = rt.engine.includes('aes-gcm');
  const age = backupAgeDays(s);
  const native = rt.engine.startsWith('sqlite');
  return (
    <section className="card card-pad" data-testid="protection">
      <div className="row spread wrap"><h3 style={{ margin: 0 }}>{t('protect.title')}</h3>{EDITION === 'personal' && <span className="chip gold" data-testid="edition-personal">{t('protect.personal_edition')}</span>}</div>
      <ul className="plain-list protect-list">
        <li data-testid="protect-storage"><Icon name={s.volatile ? 'shield' : 'check'} size={15} /><span>{s.volatile ? t('protect.volatile') : native ? t('protect.native') : t('protect.browser')}{!s.volatile && !native && s.persisted !== null ? ` ${t(s.persisted ? 'protect.persisted' : 'protect.not_persisted')}` : ''}</span></li>
        <li data-testid="protect-encryption"><Icon name="lock" size={15} /><span>{encrypted ? t('protect.encrypted') : t('protect.not_encrypted')} {!encrypted && <button className="btn btn-sm btn-ghost" onClick={() => navigate('/security')}>{t('protect.turn_on')}</button>}</span></li>
        <li data-testid="protect-snapshots"><Icon name="undo" size={15} /><span>{t('protect.snapshots')}{s.lastAuto ? ` ${t('protect.last_auto', { time: i18n.dateTime(s.lastAuto) })}` : ''}</span></li>
        <li data-testid="protect-backup" className={age === null || age >= 7 ? 'warn' : ''}><Icon name="download" size={15} /><span>{age === null ? t('protect.backup_never') : t('protect.backup_age', { days: age })} {t('protect.backup_why')}</span></li>
        <li data-testid="protect-network"><Icon name="cloud" size={15} /><span>{s.online ? t('protect.online') : t('protect.offline')} {t('protect.network_needed')}</span></li>
      </ul>
    </section>
  );
}

function ImportWizard() {
  const rt = useRuntime();
  const { t, i18n } = rt;
  const [text, setText] = useState<string | null>(null);
  const [fileName, setFileName] = useState('');
  const [password, setPassword] = useState('');
  const [candidate, setCandidate] = useState(0);
  const [busy, setBusy] = useState(false);
  const [parsed, setParsed] = useState<ParsedSource | null>(null);
  const [mode, setMode] = useState<'merge' | 'replace'>('merge');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RestoreResult | null>(null);
  const [drag, setDrag] = useState(false);
  const preview: RestorePreview | null = useMemo(() => (parsed ? previewRestore(rt.ws.data, parsed.dataset, mode) : null), [parsed, mode, rt.ws.data]);
  useEffect(() => { if (QUEUED) { setText(QUEUED.text); setFileName(QUEUED.name); QUEUED = null; } }, []);

  const load = async (f: File) => { setText(await f.text()); setFileName(f.name); setParsed(null); setResult(null); setError(null); };
  const analyze = async (idx = candidate) => {
    if (!text) return;
    setBusy(true); setError(null); setResult(null);
    try { const p = await parseSource(text, { password: password || undefined, candidateIndex: idx }); setParsed(p); if (p.kind === 'vero-os' && rt.ws.data.persons.length === 0) setMode('replace'); }
    catch (e) { setParsed(null); setError(e instanceof BackupError ? t(`errors.import.${e.code === 'newer_schema' || e.code === 'schema' || e.code === 'verify_failed' ? 'unsupported' : e.code}`) : String((e as Error).message)); }
    finally { setBusy(false); }
  };
  const commit = async () => {
    if (!preview) return;
    setBusy(true); setError(null);
    try {
      const label = `${parsed?.kind === 'legacy' ? 'legacy import' : 'restore'} · ${fileName || 'paste'}`;
      const res = await commitRestore(rt.ws, preview, { label, reason: parsed?.kind === 'legacy' ? 'pre-import' : 'pre-restore' });
      if (parsed?.kind === 'vero-os' && parsed.file.files?.length) { const fr = await restoreFiles(rt.ws, parsed.file.files); if (fr.corrupt.length) rt.toast(t('data.files_corrupt', { count: fr.corrupt.length }), 'error'); }
      if (parsed?.kind === 'legacy' && rt.demo) await rt.ws.run((v, c) => saveSetting(v, 'demo', false, c));
      setResult(res); setParsed(null); setText(null);
      rt.toast(t('data.done'));
    } catch (e) { setError(t('data.verify_fail') + (e instanceof Error ? ` (${e.message})` : '')); }
    finally { setBusy(false); }
  };
  const report = parsed?.kind === 'legacy' ? parsed.analysis.report : null;
  const step = result ? 4 : preview ? 3 : text ? 2 : 1;
  return (
    <section className="card card-pad" data-testid="import-wizard">
      <div className="row spread wrap" style={{ marginBottom: 12 }}>
        <div><h2 style={{ margin: 0, fontSize: 18 }}>{t('data.import_title')}</h2><p className="muted" style={{ margin: '4px 0 0', maxWidth: 760 }}>{t('data.import_desc')}</p></div>
        <a className="btn btn-sm" href="legacy-exporter.html" target="_blank" rel="noreferrer" title={t('data.exporter_desc')}><Icon name="shield" />{t('data.exporter_title')}</a>
      </div>
      <div className="steps">{['data.choose_file', 'data.analyze', 'data.preview', 'data.done'].map((k, i) => <span key={k} className={step === i + 1 ? 'on' : ''}>{i + 1}. {t(k)}</span>)}</div>

      <label className={`drop${drag ? ' active' : ''}`} onDragOver={(e: DragEvent) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={(e: DragEvent) => { e.preventDefault(); setDrag(false); const f = e.dataTransfer?.files?.[0]; if (f) load(f); }} style={{ display: 'block', cursor: 'pointer' }}>
        <input type="file" accept=".json,.icrm,.txt,application/json" className="sr-only" data-testid="import-file" onChange={(e: { target: HTMLInputElement }) => { const f = e.target.files?.[0]; if (f) load(f); }} />
        <Icon name="upload" size={26} /><div><b>{fileName || t('data.choose_file')}</b></div>
        {text && <div className="small">{i18n.number(text.length)} bytes</div>}
      </label>
      <div className="row wrap" style={{ marginTop: 12 }}>
        <input className="input" style={{ maxWidth: 320 }} type="password" placeholder={t('data.password')} value={password} onChange={(e: { target: HTMLInputElement }) => setPassword(e.target.value)} />
        <button className="btn btn-primary" disabled={!text || busy} onClick={() => analyze()} data-testid="analyze">{busy && !parsed ? t('data.analyzing') : t('data.analyze')}</button>
      </div>
      {error && <div className="banner" style={{ borderColor: 'var(--crit)', marginTop: 14 }} role="alert">{error}</div>}

      {parsed && preview && (
        <div style={{ marginTop: 18 }} data-testid="import-preview">
          <div className="row wrap" style={{ gap: 8, marginBottom: 12 }}>
            <span className="chip gold">{t('data.detected', { source: parsed.kind === 'legacy' ? `VÉRO CRM ${report!.sourceVersion}` : `AVRASTRO OS backup · ${parsed.file.createdAt.slice(0, 10)}` })}</span>
            {parsed.kind === 'legacy' && parsed.analysis.source.candidates.length > 1 && (
              <select className="select" style={{ width: 'auto' }} value={candidate} onChange={(e: { target: HTMLSelectElement }) => { const i = Number(e.target.value); setCandidate(i); analyze(i); }}>
                {parsed.analysis.source.candidates.map((c, i) => <option key={i} value={i}>{c.origin} · {c.clients} / {c.tasks}</option>)}
              </select>
            )}
            {report && <span className={`chip ${report.warnings.length ? 'high' : 'ok'}`}>{t('data.warnings', { count: report.warnings.length })}</span>}
            {preview.conflicts.length > 0 && <span className="chip high">{preview.conflicts.length} ⚑</span>}
          </div>
          <div className="grid grid-2">
            <table className="table">
              <thead><tr><th>{t('data.table')}</th><th className="num">{t('data.added')}</th><th className="num">{t('data.changed')}</th><th className="num">{t('data.removed')}</th><th className="num">{t('data.unchanged')}</th></tr></thead>
              <tbody>{MAIN_TABLES.map((tb) => { const d = preview.diff[tb]; return (d.added + d.changed + d.removed + d.unchanged) ? <tr key={tb}><td>{t(`data.stats.${tb}`)}</td><td className="num">{d.added}</td><td className="num">{d.changed}</td><td className="num" style={{ color: d.removed ? 'var(--crit)' : undefined }}>{d.removed}</td><td className="num">{d.unchanged}</td></tr> : null; })}</tbody>
            </table>
            <div className="stack">
              {report && report.warnings.length > 0 && <div className="warn-list card card-pad" style={{ boxShadow: 'none' }}>{report.warnings.map((w, i) => <div key={i} className="small" style={{ marginBottom: 6 }}><span className="chip high">{w.count && w.count > 1 ? `×${w.count}` : '!'}</span> {w.message}{w.ref ? <span className="muted"> — {w.ref}</span> : null}</div>)}</div>}
              {report && report.excludedSecrets.length > 0 && <div className="small muted"><Icon name="shield" size={14} /> {report.excludedSecrets.join(', ')}</div>}
              <Field label={t('data.mode')} group>
                <div className="segmented"><button aria-pressed={mode === 'merge'} onClick={() => setMode('merge')}>{t('data.mode_merge')}</button><button aria-pressed={mode === 'replace'} onClick={() => setMode('replace')}>{t('data.mode_replace')}</button></div>
              </Field>
              <div className="small muted">{preview.totals.before} → <b style={{ color: 'var(--text)' }}>{preview.totals.after}</b> · {t('data.integrity')}: {preview.issues.length ? <span style={{ color: 'var(--crit)' }}>{t('data.integrity_issues', { count: preview.issues.length })}</span> : t('data.integrity_ok')}</div>
              <button className="btn btn-primary" disabled={busy || preview.issues.length > 0} onClick={commit} data-testid="commit-import">{busy ? t('data.committing') : t('data.commit')}</button>
            </div>
          </div>
        </div>
      )}
      {result && (
        <div className="banner" style={{ marginTop: 16, borderStyle: 'solid', borderColor: 'var(--ok)' }} data-testid="import-result">
          <div><b>{t('data.done')}</b><div className="small">{t('data.verify_ok', { count: result.records })} · {t('data.safety', { label: result.snapshot.label })}</div></div>
        </div>
      )}
    </section>
  );
}

export function BackupCard() {
  const rt = useRuntime();
  const { t } = rt;
  const [pw, setPw] = useState('');
  const exportPlain = async () => { const b = await createFullBackup(rt.ws, { label: 'manual' }); download(`vero-backup-${stamp()}.vero.json`, serializeBackup(b)); markBackupExport(); rt.toast(t('common.saved')); };
  const exportSecure = async () => { const b = await createFullBackup(rt.ws, { label: 'manual' }); download(`vero-backup-${stamp()}.vero.enc.json`, await encryptBackup(serializeBackup(b), pw)); setPw(''); markBackupExport(); rt.toast(t('common.saved')); };
  return (
    <section className="card card-pad">
      <h3 style={{ margin: '0 0 4px' }}>{t('data.backup_title')}</h3>
      <p className="muted small" style={{ marginTop: 0 }}>{t('data.backup_desc')}</p>
      <button className="btn btn-primary" onClick={exportPlain} data-testid="export-backup"><Icon name="download" />{t('data.export_plain')}</button>
      <hr className="sep" />
      <div className="row wrap"><input className="input" style={{ maxWidth: 260 }} type="password" placeholder={t('data.secure_password')} value={pw} onChange={(e: { target: HTMLInputElement }) => setPw(e.target.value)} /><button className="btn" disabled={pw.length < 10} onClick={exportSecure}><Icon name="shield" />{t('data.export_secure')}</button></div>
      <p className="muted small">{t('data.restore_desc')}</p>
    </section>
  );
}

function Snapshots() {
  const rt = useRuntime();
  const { t, i18n } = rt;
  const [list, setList] = useState<SnapshotMeta[]>([]);
  const confirm = useConfirm();
  const refresh = () => rt.ws.adapter.listSnapshots().then(setList);
  useEffect(() => { refresh(); return rt.ws.subscribe(() => { refresh(); }); }, []);
  return (
    <section className="card card-pad" data-testid="snapshots">
      <h3 style={{ margin: '0 0 8px' }}>{t('data.snapshots')}</h3>
      {list.length ? <table className="table"><tbody>{list.slice(0, 12).map((s) => (
        <tr key={s.id}><td><div>{s.label}</div><div className="tiny muted">{i18n.dateTime(s.createdAt)} · {s.records} · {(s.bytes / 1024).toFixed(0)} KB</div></td>
          <td className="num"><div className="row" style={{ justifyContent: 'flex-end' }}>
            <button className="btn btn-sm btn-icon" aria-label={t('common.download')} onClick={async () => { const c = await rt.ws.adapter.readSnapshot(s.id); if (c) download(`vero-snapshot-${s.createdAt.slice(0, 10)}.vero.json`, c); }}><Icon name="download" /></button>
            <button className="btn btn-sm" onClick={() => confirm(t('data.rollback_confirm', { label: s.label }), async () => { try { await rollbackToSnapshot(rt.ws, s.id); rt.toast(t('data.done')); refresh(); } catch { rt.toast(t('data.verify_fail'), 'error'); } }, true)}><Icon name="undo" />{t('data.rollback')}</button>
          </div></td></tr>
      ))}</tbody></table> : <Empty icon="shield">{t('data.snapshots_empty')}</Empty>}
    </section>
  );
}

function Diagnostics() {
  const rt = useRuntime();
  const { t, i18n } = rt;
  const [issues, setIssues] = useState<number | null>(null);
  const counts = rt.ws.counts();
  const tr = useMemo(() => catalogReport(), []);
  const run = () => setIssues(validateDataset(rt.ws.data).length);
  const bundle = () => download(`vero-diagnostics-${stamp()}.json`, JSON.stringify({ at: new Date().toISOString(), schemaVersion: rt.ws.schemaVersion, engine: rt.engine, locale: rt.locale, counts, errors: errorLog(), missingI18n: rt.i18n.missing(), userAgent: navigator.userAgent }, null, 2));
  return (
    <section className="card card-pad" data-testid="diagnostics">
      <div className="row spread wrap"><h3 style={{ margin: 0 }}>{t('data.diagnostics')}</h3>
        <div className="row"><button className="btn btn-sm" onClick={run} data-testid="integrity-check">{t('data.integrity')}</button><button className="btn btn-sm" onClick={bundle}><Icon name="download" />{t('common.export')}</button></div></div>
      <div className="row wrap small muted" style={{ margin: '8px 0 12px', gap: 14 }}>
        <span>{t('data.schema', { version: rt.ws.schemaVersion })}</span><span>{t('data.storage', { engine: rt.engine })}</span>
        {issues !== null && <span style={{ color: issues ? 'var(--crit)' : 'var(--ok)' }} data-testid="integrity-result">{issues ? t('data.integrity_issues', { count: issues }) : t('data.integrity_ok')}</span>}
      </div>
      <div className="grid grid-2">
        <table className="table"><tbody>{TABLE_NAMES.map((tb) => <tr key={tb}><td>{tb}</td><td className="num">{i18n.number(counts[tb])}</td></tr>)}</tbody></table>
        <table className="table"><thead><tr><th>{t('data.translations')}</th><th className="num">%</th></tr></thead><tbody>{tr.map((r) => <tr key={r.code}><td>{LOCALES.find((l) => l.code === r.code)?.name} <span className="tiny muted">{r.status}</span></td><td className="num">{Math.round(r.coverage * 100)}</td></tr>)}</tbody></table>
      </div>
    </section>
  );
}
